import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getSlaPolicies, saveSlaPolicies, recalculateSla, getTicketProjects, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { PageHeading } from '../components/NotionUI';
import Select from '../components/Select';
import { PRIORITY_COLORS } from '../components/NotionUI';

const PRIORITIES = ['urgent', 'high', 'medium', 'low'];
const DAYS = [1, 2, 3, 4, 5, 6, 7]; // 1 = lunedì … 7 = domenica
const key = (projectId, priority) => `${projectId}:${priority}`;

const DEFAULT_SCHEDULE = Object.fromEntries(DAYS.map((d) => [d, { enabled: d <= 5, start: '09:00', end: '18:00' }]));
const baseCalendar = (projectId) => ({ mode: projectId === '0' ? 'always' : 'inherit', unit: 'hours', schedule: DEFAULT_SCHEDULE });

// Pasqua (calendario gregoriano, algoritmo di Meeus/Jones/Butcher): serve per la Pasquetta.
const easterMonday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  const date = new Date(Date.UTC(year, month - 1, day + 1));
  return date.toISOString().slice(0, 10);
};

// Festività nazionali italiane: le fisse valgono "ogni anno", la Pasquetta si inserisce anno per anno.
const italianHolidays = (year) => [
  { day: `${year}-01-01`, name: 'Capodanno', yearly: true },
  { day: `${year}-01-06`, name: 'Epifania', yearly: true },
  { day: easterMonday(year), name: 'Lunedì dell\'Angelo (Pasquetta)', yearly: false },
  { day: `${year}-04-25`, name: 'Festa della Liberazione', yearly: true },
  { day: `${year}-05-01`, name: 'Festa del Lavoro', yearly: true },
  { day: `${year}-06-02`, name: 'Festa della Repubblica', yearly: true },
  { day: `${year}-08-15`, name: 'Ferragosto', yearly: true },
  { day: `${year}-11-01`, name: 'Ognissanti', yearly: true },
  { day: `${year}-12-08`, name: 'Immacolata Concezione', yearly: true },
  { day: `${year}-12-25`, name: 'Natale', yearly: true },
  { day: `${year}-12-26`, name: 'Santo Stefano', yearly: true },
];

// I tempi si salvano in minuti. In ore si scrivono anche decimali (0,5 = mezz'ora); in giorni sono interi (giorni lavorativi).
const toDisplay = (minutes, unit) => {
  if (minutes === null || minutes === undefined) return '';
  return String(Math.round((unit === 'days' ? minutes / 1440 : minutes / 60) * 100) / 100);
};
const toMinutes = (text, unit) => {
  const n = Number(String(text).replace(',', '.'));
  if (text === '' || Number.isNaN(n) || n <= 0) return null;
  return unit === 'days' ? Math.max(1, Math.round(n)) * 1440 : Math.round(n * 60);
};

function SlaSettings({ user, onLogout }) {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  // "progetto:priorità" -> { first, firstUnit, resolution, resolutionUnit }: minuti (null = nessun limite) e unità ("hours" | "days") di ogni tempo
  const [cells, setCells] = useState({});
  const [calendars, setCalendars] = useState({}); // "progetto" -> { mode, schedule }
  const [holidays, setHolidays] = useState({}); // "progetto" -> [{ day, name, yearly }]
  const [timezone, setTimezone] = useState('');
  const [scope, setScope] = useState('0');
  const [openHours, setOpenHours] = useState(false); // sezioni chiuse all'inizio: si aprono con un clic
  const [openHolidays, setOpenHolidays] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sla, proj] = await Promise.all([getSlaPolicies(), getTicketProjects()]);
      setProjects(proj.projects || []);
      const next = {};
      (sla.policies || []).forEach((p) => {
        next[key(p.project_id, p.priority)] = {
          first: p.first_response_minutes,
          firstUnit: p.first_response_unit || 'hours',
          resolution: p.resolution_minutes,
          resolutionUnit: p.resolution_unit || 'hours',
        };
      });
      setCells(next);
      const cal = {};
      (sla.calendars || []).forEach((c) => {
        cal[String(c.project_id)] = { mode: c.mode, unit: 'hours', schedule: c.schedule };
      });
      setCalendars(cal);
      const hol = {};
      (sla.holidays || []).forEach((h) => {
        (hol[String(h.project_id)] = hol[String(h.project_id)] || []).push({ day: h.day, name: h.name || '', yearly: !!h.yearly });
      });
      setHolidays(hol);
      setTimezone(sla.timezone || '');
    } catch (err) {
      setError(errorMessage(err, t('slaSettings.loadError')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const calendarOf = (projectId) => ({ ...baseCalendar(String(projectId)), ...(calendars[String(projectId)] || {}) });
  const cal = calendarOf(scope);

  const EMPTY_CELL = { first: null, firstUnit: 'hours', resolution: null, resolutionUnit: 'hours' };
  const cell = (projectId, priority) => ({ ...EMPTY_CELL, ...(cells[key(projectId, priority)] || {}) });
  const setCellFields = (priority, patch) => {
    setSuccess('');
    setCells({ ...cells, [key(scope, priority)]: { ...cell(scope, priority), ...patch } });
  };
  const setCalendar = (patch) => {
    setSuccess('');
    setCalendars({ ...calendars, [scope]: { ...cal, ...patch } });
  };
  const setDay = (day, patch) => setCalendar({ schedule: { ...cal.schedule, [day]: { ...cal.schedule[day], ...patch } } });

  const holidaysOf = (projectId) => holidays[String(projectId)] || [];
  const setHolidaysOf = (list) => {
    setSuccess('');
    setHolidays({ ...holidays, [scope]: list });
  };
  const sortedHolidays = [...holidaysOf(scope)].sort((a, b) => a.day.localeCompare(b.day));
  const addHoliday = () => setHolidaysOf([...holidaysOf(scope), { day: '', name: '', yearly: false }]);
  const updateHoliday = (item, patch) => setHolidaysOf(holidaysOf(scope).map((h) => (h === item ? { ...h, ...patch } : h)));
  const removeHoliday = (item) => setHolidaysOf(holidaysOf(scope).filter((h) => h !== item));
  const addItalianHolidays = () => {
    const year = new Date().getFullYear();
    const have = new Set(holidaysOf(scope).map((h) => h.day));
    const toAdd = [...italianHolidays(year), ...italianHolidays(year + 1).filter((h) => !h.yearly)].filter((h) => !have.has(h.day));
    setHolidaysOf([...holidaysOf(scope), ...toAdd]);
  };
  // I festivi contano solo con gli orari di apertura (propri o, per una coda, quelli dell'azienda).
  const holidaysApply = cal.mode === 'custom' || (cal.mode === 'inherit' && calendarOf('0').mode === 'custom');

  const unitLabel = (unit, value) => t(unit === 'days' ? (value === '1' ? 'slaSettings.day' : 'slaSettings.days') : (value === '1' ? 'slaSettings.hour' : 'slaSettings.hours'));

  // Riepilogo dell'orario nell'intestazione chiusa: "Lun–Ven 09:00–18:00", oppure "Sempre" / "Come l'azienda".
  const hoursSummary = (c) => {
    if (c.mode === 'always') return t('slaSettings.summaryAlways');
    if (c.mode === 'inherit') return t('slaSettings.modeInherit');
    const on = DAYS.filter((d) => c.schedule[d].enabled);
    if (on.length === 0) return t('slaSettings.closed');
    const first = c.schedule[on[0]];
    const same = on.every((d) => c.schedule[d].start === first.start && c.schedule[d].end === first.end);
    const short = (d) => t(`slaSettings.day${d}`).slice(0, 3);
    const consecutive = on.every((d, i) => i === 0 || d === on[i - 1] + 1);
    const days = on.length === 1 ? short(on[0]) : consecutive ? `${short(on[0])}–${short(on[on.length - 1])}` : t('slaSettings.nDays', { count: on.length });
    return same ? `${days} ${first.start}–${first.end}` : `${days} · ${t('slaSettings.variableHours')}`;
  };

  // Il testo che si sta scrivendo resta com'è finché non si esce dal campo; i minuti salvati si ricalcolano allo stesso momento.
  const [drafts, setDrafts] = useState({});
  const draftKey = (priority, field) => `${scope}:${priority}:${field}`;
  const fieldUnit = (priority, field) => cell(scope, priority)[`${field}Unit`];
  const fieldValue = (priority, field) => {
    const k = draftKey(priority, field);
    if (k in drafts) return drafts[k];
    return toDisplay(cell(scope, priority)[field], fieldUnit(priority, field));
  };
  const commit = (priority, field) => {
    const k = draftKey(priority, field);
    if (!(k in drafts)) return;
    setCellFields(priority, { [field]: toMinutes(drafts[k], fieldUnit(priority, field)) });
    const { [k]: removed, ...rest } = drafts; // eslint-disable-line no-unused-vars
    setDrafts(rest);
  };

  // Cambiando unità il valore già scritto si converte (ore → giorni arrotondati, minimo 1), così il significato non cambia di nascosto.
  const changeFieldUnit = (priority, field, unit) => {
    if (unit === fieldUnit(priority, field)) return;
    const minutes = cell(scope, priority)[field];
    const converted = minutes === null || minutes === undefined ? null : unit === 'days' ? Math.max(1, Math.round(minutes / 1440)) * 1440 : minutes;
    const k = draftKey(priority, field);
    const { [k]: removed, ...rest } = drafts; // eslint-disable-line no-unused-vars
    setDrafts(rest);
    setCellFields(priority, { [field]: converted, [`${field}Unit`]: unit });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Applica i campi ancora in modifica (scritti ma non confermati uscendo dal campo).
      const merged = { ...cells };
      Object.entries(drafts).forEach(([k, text]) => {
        const [s, priority, field] = k.split(':');
        const current = { ...EMPTY_CELL, ...(merged[key(s, priority)] || {}) };
        merged[key(s, priority)] = { ...current, [field]: toMinutes(text, current[`${field}Unit`]) };
      });
      setDrafts({});
      const policies = Object.entries(merged).map(([k, v]) => {
        const [projectId, priority] = k.split(':');
        return {
          project_id: Number(projectId),
          priority,
          first_response_minutes: v.first,
          first_response_unit: v.firstUnit || 'hours',
          resolution_minutes: v.resolution,
          resolution_unit: v.resolutionUnit || 'hours',
        };
      });
      const holidayRows = Object.entries(holidays).flatMap(([projectId, list]) => list.filter((h) => h.day).map((h) => ({ project_id: Number(projectId), day: h.day, name: h.name, yearly: h.yearly })));
      const cals = Object.entries(calendars).map(([projectId, c]) => ({
        project_id: Number(projectId),
        mode: c.mode,
        unit: 'hours',
        schedule: c.mode === 'custom' ? c.schedule : null,
      }));
      await saveSlaPolicies(policies, cals, holidayRows);
      setSuccess(t('slaSettings.saved'));
      await load();
    } catch (err) {
      setError(errorMessage(err, t('slaSettings.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    if (!window.confirm(t('slaSettings.recalcConfirm'))) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await recalculateSla();
      setSuccess(t('slaSettings.recalcDone', { count: res.updated }));
    } catch (err) {
      setError(errorMessage(err, t('slaSettings.recalcError')));
    } finally {
      setSaving(false);
    }
  };

  const modeOptions = scope === '0'
    ? [['always', t('slaSettings.modeCompanyAlways')], ['custom', t('slaSettings.modeCustom')]]
    : [['inherit', t('slaSettings.modeInherit')], ['always', t('slaSettings.modeQueueAlways')], ['custom', t('slaSettings.modeCustom')]];

  const inheritedText = (priority, field) => {
    if (scope === '0') return t('slaSettings.none');
    const company = cell(0, priority);
    const minutes = company[field];
    if (!minutes) return t('slaSettings.none');
    const unit = company[`${field}Unit`];
    const shown = toDisplay(minutes, unit);
    return t('slaSettings.inherits', { hours: `${shown} ${unitLabel(unit, shown)}` });
  };

  // Ci sono tempi in giorni nell'ambito scelto: si spiega come contano.
  const usesDays = PRIORITIES.some((p) => cell(scope, p).firstUnit === 'days' || cell(scope, p).resolutionUnit === 'days');
  const workingDays = cal.mode === 'custom' || (cal.mode === 'inherit' && calendarOf('0').mode === 'custom');

  const chevron = (open) => <span className={'sla-chev' + (open ? ' sla-chev-open' : '')} aria-hidden="true">›</span>;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="clock" title={t('slaSettings.title')} subtitle={t('slaSettings.subtitle')} />
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : (
          <>
            <div className="form-group" style={{ maxWidth: 360 }}>
              <label>{t('slaSettings.scope')}</label>
              <Select value={scope} onChange={(e) => { setScope(e.target.value); setDrafts({}); }}>
                <option value="0">{t('slaSettings.scopeCompany')}</option>
                {projects.map((p) => <option key={p.id} value={String(p.id)}>{t('slaSettings.scopeQueue', { name: p.name })}</option>)}
              </Select>
            </div>
            <p className="tp-hint">{scope === '0' ? t('slaSettings.hintCompany') : t('slaSettings.hintQueue')}</p>

            <section className="sla-cal">
              <button type="button" className="sla-cal-head" aria-expanded={openHours} onClick={() => setOpenHours(!openHours)}>
                <span className="sla-cal-title">{t('slaSettings.calendarTitle')}</span>
                <span className="sla-cal-summary">{hoursSummary(cal)}</span>
                {chevron(openHours)}
              </button>
              {openHours && (
                <>
                  <div className="sla-seg" role="group" aria-label={t('slaSettings.calendarTitle')}>
                    {modeOptions.map(([value, label]) => (
                      <button key={value} type="button" className={cal.mode === value ? 'sla-seg-on' : ''} aria-pressed={cal.mode === value} onClick={() => setCalendar({ mode: value })}>{label}</button>
                    ))}
                  </div>
                  {cal.mode === 'custom' && (
                    <>
                      <div className="sla-days">
                        {DAYS.map((d) => {
                          const day = cal.schedule[d];
                          return (
                            <div key={d} className={'sla-day' + (day.enabled ? '' : ' sla-day-off')}>
                              <label className="ksw-row sla-day-name">
                                <span className="ksw"><input type="checkbox" checked={day.enabled} onChange={(e) => setDay(d, { enabled: e.target.checked })} /><span className="ksw-slider" /></span>
                                <span>{t(`slaSettings.day${d}`)}</span>
                              </label>
                              {day.enabled ? (
                                <span className="sla-day-hours">
                                  <input type="time" className="sla-time" value={day.start} onChange={(e) => setDay(d, { start: e.target.value })} aria-label={t('slaSettings.opensAt')} />
                                  <span className="nt-muted">{t('slaSettings.to')}</span>
                                  <input type="time" className="sla-time" value={day.end} onChange={(e) => setDay(d, { end: e.target.value })} aria-label={t('slaSettings.closesAt')} />
                                </span>
                              ) : (
                                <span className="nt-muted sla-day-closed">{t('slaSettings.closed')}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {timezone && <p className="tp-hint">{t('slaSettings.timezoneHint', { zone: timezone })}</p>}
                    </>
                  )}
                  {cal.mode === 'inherit' && <p className="tp-hint">{t('slaSettings.inheritHint')}</p>}
                  {cal.mode === 'always' && <p className="tp-hint">{t('slaSettings.alwaysHint')}</p>}
                </>
              )}
            </section>

            {holidaysApply && (
              <section className="sla-cal">
                <button type="button" className="sla-cal-head" aria-expanded={openHolidays} onClick={() => setOpenHolidays(!openHolidays)}>
                  <span className="sla-cal-title">{t('slaSettings.holidaysTitle')}</span>
                  <span className="sla-cal-summary">{sortedHolidays.length === 0 ? t('slaSettings.holidaysNone') : sortedHolidays.length === 1 ? t('slaSettings.holidaysOne') : t('slaSettings.holidaysCount', { count: sortedHolidays.length })}</span>
                  {chevron(openHolidays)}
                </button>
                {openHolidays && (
                  <>
                    <p className="tp-hint">{scope === '0' ? t('slaSettings.holidaysHint') : t('slaSettings.holidaysHintQueue')}</p>
                    {sortedHolidays.length === 0 && <p className="nt-muted sla-hol-empty">{t('slaSettings.holidaysEmpty')}</p>}
                    <div className="sla-hols">
                      {sortedHolidays.map((h, i) => (
                        <div key={i} className="sla-hol">
                          <input type="date" className="sla-time sla-hol-date" value={h.day} onChange={(e) => updateHoliday(h, { day: e.target.value })} aria-label={t('slaSettings.holidayDate')} />
                          <input type="text" className="sla-input sla-hol-name" maxLength={100} value={h.name} placeholder={t('slaSettings.holidayName')} aria-label={t('slaSettings.holidayName')} onChange={(e) => updateHoliday(h, { name: e.target.value })} />
                          <label className="ksw-row sla-hol-yearly">
                            <span className="ksw"><input type="checkbox" checked={h.yearly} onChange={(e) => updateHoliday(h, { yearly: e.target.checked })} /><span className="ksw-slider" /></span>
                            <span>{t('slaSettings.holidayYearly')}</span>
                          </label>
                          <button type="button" className="icon-btn icon-btn-danger" onClick={() => removeHoliday(h)} title={t('common.delete')} aria-label={t('common.delete')}>×</button>
                        </div>
                      ))}
                    </div>
                    <div className="sla-hol-actions">
                      <button type="button" className="btn btn-secondary btn-sm" onClick={addHoliday}>+ {t('slaSettings.holidayAdd')}</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={addItalianHolidays}>{t('slaSettings.holidaysItalian')}</button>
                    </div>
                  </>
                )}
              </section>
            )}

            <div className="nt-table-wrap">
              <table className="nt-table cu-table sla-edit">
                <thead>
                  <tr>
                    <th>{t('slaSettings.priority')}</th>
                    <th>{t('slaSettings.firstResponse')}</th>
                    <th>{t('slaSettings.resolution')}</th>
                  </tr>
                </thead>
                <tbody>
                  {PRIORITIES.map((p) => (
                    <tr key={p}>
                      <td><span className="tk-prio" style={{ '--c': PRIORITY_COLORS[p] }}><i aria-hidden="true" /> {t(`tickets.priority_${p}`)}</span></td>
                      {['first', 'resolution'].map((field) => (
                        <td key={field}>
                          <span className="sla-field">
                            <input
                              type="text"
                              inputMode="decimal"
                              className="sla-input"
                              value={fieldValue(p, field)}
                              placeholder={inheritedText(p, field)}
                              onChange={(e) => setDrafts({ ...drafts, [draftKey(p, field)]: e.target.value })}
                              onBlur={() => commit(p, field)}
                            />
                            <select className="sla-unit-select" value={fieldUnit(p, field)} onChange={(e) => changeFieldUnit(p, field, e.target.value)} aria-label={t('slaSettings.unitLabel')}>
                              <option value="hours">{t('slaSettings.hours')}</option>
                              <option value="days">{t('slaSettings.days')}</option>
                            </select>
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {usesDays && <p className="tp-hint">{workingDays ? t('slaSettings.unitDaysHintWork') : t('slaSettings.unitDaysHintAll')}</p>}
            <p className="tp-hint">{t('slaSettings.note')}</p>
            <div className="tp-savebar">
              <button type="button" className="btn btn-secondary" disabled={saving} onClick={handleRecalculate} title={t('slaSettings.recalcHint')}>{t('slaSettings.recalc')}</button>
              <button type="button" className="btn" disabled={saving} onClick={handleSave}>{saving ? t('common.saving') : t('common.save')}</button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default SlaSettings;
